import { MainLayout } from "./MainLayout";
import { useApp } from "./AppContext";
import { useAuth } from "./AuthContext";
import { useGetExtra } from "../api";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { useTranslation } from "../i18n/TranslationContext";
import { useAppRouter } from "../hooks/useAppRouter";
import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { PayrollNotification } from "./PayrollNotification";

export function TaskDashboard() {
	const { t } = useTranslation();
	const router = useAppRouter();
	const {
		getDashboardTasks,
		userProfile,
		canAccessScreen,
		jobs,
		setSelectedJob,
		getEventStatus,
		getProfileCompletionProgress,
		updateOnboardingState,
		userCompletionState,
		logout,
	} = useApp();
	const { resetAuth } = useAuth();

	// Get current user to fetch their ID
	const { user } = useCurrentUser();

	// Fetch extra data from API to check profile completion status
	const { extra } = useGetExtra(user?.extraId);

	// Check if profile is complete based on backend data
	const isProfileComplete = extra?.profile_status === "ready_to_work";

	// Check if external user has incomplete payroll data
	const shouldShowPayrollNotification = 
		extra && 
		!extra.internal_extra && 
		!extra.payroll_completed;

	// Mock notification count (in real app, this would come from context or API)
	const getUnreadNotificationCount = () => 3; // Shows 3 unread notifications
	// Filter tasks based on user type
	const allTasks = getDashboardTasks();
	const tasks = user?.internal_extra
		? allTasks.filter((task) => task.id !== "contract" && task.id !== "payroll")
		: allTasks;

	// All tasks are now main dashboard tasks (notifications moved to header)
	const mainTasks = tasks;

	// Proper logout function
	const handleLogout = () => {
		// Use the centralized logout function
		logout();

		// Clear Auth Context (this will reset to step 0 - SignIn)
		resetAuth();

		// Navigate to sign-in page using router
		router.replace("auth-sign-in");
	};

	// Get icon component for main dashboard cards
	const getTaskIcon = (taskId: string) => {
		const iconSize = 16;

		switch (taskId) {
			default:
				return null;
		}
	};

	// Dashboard Card Component
	const DashboardCard = ({ task }: { task: any }) => {
		const handleClick = () => {
			handleTaskClick(task);
		};

		// Check if this is the profile task and if it's complete
		const isProfileTask = task.id === "profile-completion";
		const isProfileTaskComplete = isProfileTask && isProfileComplete;

		// Determine the title to display
		// If task.title looks like a translation key, translate it; otherwise use it as-is
		const translatedTitle = task.title.includes(".")
			? t(task.title)
			: task.title;
		const displayTitle = isProfileTaskComplete
			? t("common.profile")
			: translatedTitle;

		return (
			<div
				className={`dashboard-card ${task.id === "profile-completion" ? "profile-completion" : ""}`}
				onClick={handleClick}
			>
				<div className="card-content">
					<div className="card-text">
						<h3 className="card-title flex items-center text-[15px]">
							{getTaskIcon(task.id)}
							{displayTitle}
							{/* {task.id === "apply-event" && task.status === "completed" && (
								<span className="inline-flex items-center ml-2">
									<CheckCircle size={16} color="#10B981" />
								</span>
							)}
							{isProfileTaskComplete && (
								<span className="inline-flex items-center ml-2">
									<CheckCircle size={16} color="#10B981" />
								</span>
							)} */}
							{/* {task.id === "notifications" &&
								getUnreadNotificationCount() > 0 && (
									<span className="inline-flex items-center ml-2">
										<span className="bg-[var(--ucm-error-red)] text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
											{getUnreadNotificationCount()}
										</span>
									</span>
								)} */}
						</h3>
					</div>
					<div className="card-arrow">
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
							<path
								d="M8.59 16.59L13.17 12L8.59 7.41L10 6l6 6l-6 6l-1.41-1.41Z"
								fill="var(--ucm-text-light)"
							/>
						</svg>
					</div>
				</div>

				{task.showProgress &&
					task.progressValue !== undefined &&
					!isProfileTaskComplete && (
						<div className="card-progress">
							<div className="progress-bar">
								<div
									className="progress-fill"
									style={{
										width: `${isProfileTaskComplete ? 100 : task.progressValue}%`,
									}}
								/>
							</div>
						</div>
					)}
			</div>
		);
	};

	const getStatusIcon = (status: "completed" | "action-required" | "info") => {
		switch (status) {
			case "completed":
				return "✅";
			case "action-required":
				return "⚠️";
			case "info":
				return "📄";
			default:
				return "📄";
		}
	};

	const getStatusColor = (status: "completed" | "action-required" | "info") => {
		switch (status) {
			case "completed":
				return "bg-[var(--ucm-success-green)] text-white";
			case "action-required":
				return "bg-[var(--ucm-warning-orange)] text-white";
			case "info":
				return "bg-[var(--ucm-light-blue)] text-[var(--ucm-dark-blue)]";
			default:
				return "bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)]";
		}
	};

	// Event status badge functions
	const getEventStatusBadge = (taskId: string) => {
		if (taskId === "event-details") {
			// Combined Event Details
			// For the combined event card, we don't show status badges since
			// the card status itself reflects the combined status
			return null;
		}
		return null;
	};

	const getStatusBadgeConfig = (
		status:
			| "unavailable"
			| "contract-missing"
			| "checkin-ready"
			| "ready-work"
			| "approved"
			| "payrolled"
	) => {
		switch (status) {
			case "unavailable":
				return {
					text: "Unavailable",
					className:
						"bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] border-[var(--ucm-border-medium)]",
				};
			case "contract-missing":
				return {
					text: "Contract Missing",
					className:
						"bg-red-50 text-[var(--ucm-error-red)] border-[var(--ucm-error-red)]",
				};
			case "checkin-ready":
				return {
					text: "Pre-work Check-in Ready",
					className:
						"bg-[var(--ucm-light-orange)] text-[var(--ucm-dark-orange)] border-[var(--ucm-warning-orange)]",
				};
			case "ready-work":
				return {
					text: "Ready for work",
					className:
						"bg-[var(--ucm-light-blue)] text-[var(--ucm-dark-blue)] border-[var(--ucm-info-blue)]",
				};
			case "approved":
				return {
					text: "Approved",
					className:
						"bg-[var(--ucm-light-green)] text-[var(--ucm-dark-green)] border-[var(--ucm-success-green)]",
				};
			case "payrolled":
				return {
					text: "Payrolled",
					className:
						"bg-purple-50 text-[var(--ucm-booked-purple-dark)] border-[var(--ucm-booked-purple)]",
				};
			default:
				return null;
		}
	};

	const handleTaskClick = (task: any) => {
		// Special handling for Event Details task - set selected job
		if (task.id === "event-details-combined" && task.screen === "my-event") {
			// Set the first available job as selected for the event details view
			if (jobs && jobs.length > 0) {
				setSelectedJob(jobs[0]);
			}
		}

		// Special handling for group task when user is solo
		if (task.id === "your-group" && task.screen === "app-group") {
			// Show solo participation info instead of navigating
			const participationType = localStorage.getItem("participationType");
			if (participationType === "solo") {
				const confirmChange = window.confirm(
					"You are currently set for solo participation. Would you like to change to group participation instead?"
				);
				if (confirmChange) {
					localStorage.removeItem("participationType");
					router.navigateWithCheck("app-group-select");
				}
				return;
			}
		}

		const accessCheck = canAccessScreen(task.screen);

		// Show requirements if any, but still allow navigation for flexible screens
		if (accessCheck.requirements && accessCheck.requirements.length > 0) {
			console.log(`Task requirements: ${accessCheck.requirements.join(", ")}`);
		}

		router.navigateWithCheck(task.screen);
	};

	// Set hasCompletedOnboarding to true when user reaches this screen
	useEffect(() => {
		updateOnboardingState({
			hasCompletedOnboarding: true,
			isFirstTimeUser: false,
		});
	}, []);

	return (
		<MainLayout showBottomNav={true}>
			{/* Welcome, Header with Messages Icon */}
			<div className="mb-6 flex items-center justify-between">
				<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)]">
					{userProfile
						? t("taskDashboard.welcomeWithName", {
								name: userProfile.firstName + " " + userProfile.lastName,
							})
						: t("taskDashboard.welcome")}
					!
				</h1>

				{/* Messages Icon in Top Right */}
				{/* <TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								onClick={() => router.navigateWithCheck("app-notifications")}
								className="relative flex items-center justify-center w-12 h-12 bg-white border border-[var(--ucm-border-medium)] rounded-2xl hover:bg-gray-50 transition-all duration-200"
							>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
									<path
										d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"
										fill="var(--ucm-info-blue)"
									/>
								</svg>
								{getUnreadNotificationCount() > 0 && (
									<div className="absolute bg-[#e8403e] box-border content-stretch flex flex-row items-center justify-center right-1 top-1 max-w-[34px] min-w-4 overflow-clip px-1 py-0 rounded-[100px]">
										<div className="basis-0 flex flex-col font-medium grow justify-center leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#ffffff] text-[11px] text-center tracking-[0.5px]">
											<p className="block leading-[16px]">
												{getUnreadNotificationCount()}
											</p>
										</div>
									</div>
								)}
							</button>
						</TooltipTrigger>
						<TooltipContent>
							<p>{t("taskDashboard.viewNotifications")}</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider> */}
			</div>

			{/* Payroll Data Notification for External Users */}
			{shouldShowPayrollNotification && (
				<PayrollNotification />
			)}

			{/* Task Grid */}
			<div className="dashboard-container">
				{mainTasks.map((task) => (
					<DashboardCard key={task.id} task={task} />
				))}
			</div>

			{/* Logout Button */}
			<div className="mt-8 pb-6">
				<button
					onClick={handleLogout}
					className="w-full bg-white border border-[var(--ucm-border-medium)] text-[var(--ucm-text-dark)] py-4 px-6 rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] transition-all duration-200 font-medium"
				>
					{t("common.logout")}
				</button>
			</div>
		</MainLayout>
	);
}
